import { useEffect, useRef, useState } from "react";
import { CROSSHAIR_LABELS, CROSSHAIR_STYLES, CROSSHAIR_LAYERS, DEFAULT_CROSSHAIR_LAYERS, CROSSHAIR_TUNING_LIMITS, DEFAULT_CROSSHAIR_TUNING } from "../../utils/canvasStatus";

// The strip under the Layout Canvas: which tool is armed, where the cursor is, the scale, how much is on this
// drawing, and whether the work is saved. The cursor readout is written straight into the element handed in as
// `cursorRef` by the canvas's pointer handler — it changes on every mouse move and must not re-render the canvas.
// The crosshair control opens a small menu: the crosshair's style and which extra layers it shows.

const cell = { display: "flex", alignItems: "center", gap: 6, padding: "0 10px", borderRight: "1px solid var(--cs-border)", whiteSpace: "nowrap" };

function CrosshairMenu({ crosshair, onCrosshairStyle, layers, onLayersChange, tuning, onTuningChange, compact }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const on = layers || DEFAULT_CROSSHAIR_LAYERS;
  return (
    <div ref={wrapRef} style={{ position: "relative", display: "flex" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        title="Crosshair — style and what it shows"
        style={{ ...cell, cursor: "pointer", background: open ? "var(--surface)" : "transparent", border: "none", borderLeft: "1px solid var(--cs-border)", borderRight: "1px solid var(--cs-border)", font: "inherit", color: "var(--text2)" }}
        data-testid="status-crosshair"
      >
        {compact ? "✛ ▴" : `✛ ${CROSSHAIR_LABELS[crosshair]} ▴`}
      </button>
      {open && (
        <div role="menu" data-testid="crosshair-menu" style={{ position: "absolute", bottom: "100%", right: 0, width: 300, padding: 10, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.25)", fontFamily: "inherit", whiteSpace: "normal", zIndex: 30 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)", marginBottom: 6 }}>Style</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {CROSSHAIR_STYLES.map(s => (
              <button key={s} type="button" onClick={() => onCrosshairStyle(s)} aria-pressed={crosshair === s}
                style={{ flex: 1, fontSize: 12, fontWeight: 600, padding: "4px 0", borderRadius: 6, cursor: "pointer", border: `1px solid ${crosshair === s ? "#2563eb" : "var(--cs-border)"}`, background: crosshair === s ? "#2563eb" : "var(--surface)", color: crosshair === s ? "#fff" : "var(--text)" }}>
                {CROSSHAIR_LABELS[s]}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", margin: "-4px 0 8px" }}>Precision = short arms and a centre target, for tight work; the others span the whole drawing.</div>
          {onTuningChange && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)", marginBottom: 4 }}>Fine tune</div>
              {[["width", "Line thickness", 0.25, "px", 2], ["opacity", "Opacity", 0.05, "", 0.9], ["arm", "Precision arm length", 5, "px", 80]].map(([k, label, step, unit, fallback]) => {
                const [lo, hi] = CROSSHAIR_TUNING_LIMITS[k];
                const val = (tuning || DEFAULT_CROSSHAIR_TUNING)[k];
                const isSet = val !== null && val !== undefined;
                const off = crosshair === "off" || (k === "arm" && crosshair !== "precision");
                return (
                  <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "2px 0", opacity: off ? 0.5 : 1 }}>
                    <span style={{ width: 120 }}>{label}</span>
                    <input type="range" aria-label={label} min={lo} max={hi} step={step} value={isSet ? val : fallback} disabled={off}
                      onChange={e => onTuningChange({ ...(tuning || DEFAULT_CROSSHAIR_TUNING), [k]: +e.target.value })} style={{ flex: 1 }} />
                    <span style={{ width: 38, textAlign: "right", color: "var(--text3)" }}>{isSet ? val + unit : "auto"}</span>
                  </label>
                );
              })}
              <button type="button" onClick={() => onTuningChange({ ...DEFAULT_CROSSHAIR_TUNING })} style={{ fontSize: 11, marginTop: 4, cursor: "pointer", background: "transparent", border: "none", color: "var(--cs-blue)", padding: 0 }}>Reset fine tuning</button>
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)", marginBottom: 4 }}>Shows</div>
          {CROSSHAIR_LAYERS.map(l => (
            <label key={l.key} title={l.hint} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "3px 0", fontSize: 12, color: crosshair === "off" ? "var(--text3)" : "var(--text)", cursor: "pointer" }}>
              <input type="checkbox" checked={!!on[l.key]} disabled={crosshair === "off"} onChange={e => onLayersChange({ ...on, [l.key]: e.target.checked })} style={{ marginTop: 2 }} />
              <span><b style={{ fontWeight: 600 }}>{l.label}</b><br /><span style={{ color: "var(--text3)", fontSize: 11 }}>{l.hint}</span></span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CanvasStatusBar({
  mode, cursorRef, scale, needsScale, info, units, onToggleUnits, onScaleClick, shapeText, saveState, readOnlyReason, message,
  crosshair, onCrosshairStyle, layers, onLayersChange, tuning, onTuningChange, compact,
}) {
  const saveText = readOnlyReason ? readOnlyReason : saveState === "saving" ? "saving…" : "saved";
  const saveColor = readOnlyReason ? "#b45309" : saveState === "saving" ? "#b45309" : "#15803d";
  return (
    <div
      role="status"
      aria-label="Canvas status"
      style={{ display: "flex", alignItems: "stretch", height: 26, fontSize: 11.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", background: "var(--surface2, var(--surface))", borderTop: "1px solid var(--cs-border)", color: "var(--text2)", position: "relative" }}
    >
      <div style={{ ...cell, fontWeight: 700, color: "var(--cs-blue)", minWidth: compact ? 60 : 120 }} data-testid="status-mode">{mode}</div>
      <div style={{ ...cell, minWidth: compact ? 140 : 190 }} title="Cursor position on the drawing"><span ref={cursorRef} data-testid="status-cursor">—</span></div>
      <button
        type="button"
        onClick={onScaleClick}
        title={scale.set ? "This drawing's scale — click to change it" : "No scale set — measuring is off until you set one"}
        style={{ ...cell, cursor: "pointer", background: "transparent", border: "none", borderRight: "1px solid var(--cs-border)", font: "inherit",
          color: scale.set ? "#15803d" : "#dc2626", fontWeight: 700,
          outline: scale.set ? "none" : "1px dashed #dc2626", outlineOffset: -3 }}
        data-testid="status-scale"
      >
        {scale.set ? `✓ ${scale.text}` : scale.text}
      </button>
      <div style={{ ...cell, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", color: message || needsScale ? "#dc2626" : info ? "var(--cs-blue)" : "var(--text3)", fontWeight: message || needsScale || info ? 700 : 400 }} data-testid="status-message">
        {message || (needsScale ? "Set the scale for this drawing first — measuring tools are off." : (info || ""))}
      </div>
      {onToggleUnits && (
        <button type="button" onClick={onToggleUnits} data-testid="status-units" title="Display units — feet / inches or metric (metres, millimetres). A display setting only: everything is stored and priced in feet."
          style={{ ...cell, cursor: "pointer", background: "transparent", border: "none", borderLeft: "1px solid var(--cs-border)", borderRight: "1px solid var(--cs-border)", font: "inherit", color: "var(--text2)", fontWeight: 700 }}>
          {units === "metric" ? "m · m²" : "ft · in"}
        </button>
      )}
      <CrosshairMenu crosshair={crosshair} onCrosshairStyle={onCrosshairStyle} layers={layers} onLayersChange={onLayersChange} tuning={tuning} onTuningChange={onTuningChange} compact={compact} />
      {!compact && <div style={cell} data-testid="status-shapes">{shapeText}</div>}
      <div style={{ ...cell, borderRight: "none", fontWeight: 700, color: saveColor }} data-testid="status-saved">{saveText}</div>
    </div>
  );
}
